import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  X,
  Search,
  Stethoscope,
  UserRound,
  Wallet,
  CheckCircle2,
  CalendarPlus,
} from "lucide-react";

const SpecialistsDialog = ({ open, setOpen, patient }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [booked, setBooked] = useState({});
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const fetchSpecialists = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/specialists`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setData(res.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load specialists");
    } finally {
      setLoading(false);
    }
  };

  const bookConsultation = async (doc) => {
    try {
      setBookingId(doc.id);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/book-specialist`,
        {
          doctor_id: doc.id,
          client_id: patient._id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (res.data.success) {
        setBooked((prev) => ({ ...prev, [doc.id]: true }));
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Booking failed");
    } finally {
      setBookingId(null);
    }
  };

  useEffect(() => {
    if (open) fetchSpecialists();
  }, [open]);

  const filteredData = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return data;

    return data
      .map((group) => {
        const specialtyMatch = group.specialty?.toLowerCase().includes(value);

        const doctors = specialtyMatch
          ? group.doctors
          : group.doctors.filter((doc) =>
              doc.name?.toLowerCase().includes(value)
            );

        return {
          ...group,
          doctors,
        };
      })
      .filter((group) => group.doctors.length > 0);
  }, [data, search]);

  const totalDoctors = useMemo(() => {
    return data.reduce((sum, group) => sum + Number(group.doctors?.length || 0), 0);
  }, [data]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e2e8f0",
          px: 3,
          py: 2.5,
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-[-0.03em] text-slate-950">
              Specialists
            </h2>

            <p className="mt-1 text-sm font-normal text-slate-500">
              Book a specialist consultation for {patient?.name || "this patient"}.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Stats */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 mt-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-500">Specialties</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">
              {data.length.toLocaleString()}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs text-slate-500">Doctors</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">
              {totalDoctors.toLocaleString()}
            </p>
          </div>

          <div className="hidden rounded-2xl border border-slate-200 bg-white p-4 sm:block">
            <p className="text-xs text-slate-500">Booked</p>
            <p className="mt-2 text-xl font-semibold text-slate-950">
              {Object.values(booked).filter(Boolean).length.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <label className="mb-2 block text-xs font-medium text-slate-500">
            Search
          </label>

          <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
            <Search size={16} className="text-slate-400" />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search doctor or specialty"
              className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
            />
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {loading && (
            <div className="flex items-center justify-center py-14">
              <CircularProgress size={24} sx={{ color: "#0f172a" }} />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-8 text-center">
              <p className="text-sm font-medium text-red-600">{error}</p>

              <button
                type="button"
                onClick={fetchSpecialists}
                className="mt-4 h-10 rounded-xl bg-white px-4 text-sm font-medium text-red-700 ring-1 ring-red-100 transition hover:bg-red-50"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && filteredData.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Stethoscope size={20} className="text-slate-500" />
              </div>

              <h3 className="text-sm font-semibold text-slate-950">
                No specialists found
              </h3>

              <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-slate-500">
                Try changing your search term.
              </p>
            </div>
          )}

          {!loading && !error && filteredData.length > 0 && (
            <div className="space-y-7">
              {filteredData.map((group, idx) => (
                <section key={idx}>
                  {/* Specialty Header */}
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                        <Stethoscope size={15} />
                      </div>

                      <h3 className="text-sm font-semibold text-slate-950">
                        {group.specialty || "Specialty"}
                      </h3>
                    </div>

                    <span className="text-xs text-slate-400">
                      {group.doctors.length} doctor
                      {group.doctors.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {/* Doctors */}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {group.doctors.map((doc) => {
                      const isBooking = bookingId === doc.id;
                      const isBooked = booked[doc.id];

                      return (
                        <article
                          key={doc.id}
                          className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300 hover:shadow-sm"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                              {doc.name
                                ?.split(" ")
                                .filter(Boolean)
                                .map((item) => item[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase() || "DR"}
                            </div>

                            <div className="min-w-0 flex-1">
                              <h4 className="truncate text-sm font-semibold text-slate-950">
                                {doc.name}
                              </h4>

                              <p className="mt-1 text-xs text-slate-500">
                                {group.specialty}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                              <p className="flex items-center gap-1 text-xs text-slate-500">
                                <UserRound size={13} />
                                Consultations
                              </p>
                              <p className="mt-1 text-sm font-semibold text-slate-950">
                                {Number(doc.consultations || 0).toLocaleString()}
                              </p>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                              <p className="flex items-center gap-1 text-xs text-slate-500">
                                <Wallet size={13} />
                                Charge
                              </p>
                              <p className="mt-1 text-sm font-semibold text-slate-950">
                                ₦{Number(doc.price || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            disabled={isBooking || isBooked}
                            onClick={() => bookConsultation(doc)}
                            className={`mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-xl text-sm font-medium transition ${
                              isBooked
                                ? "bg-emerald-50 text-emerald-700"
                                : isBooking
                                ? "cursor-not-allowed bg-slate-100 text-slate-400"
                                : "bg-[#075E54] text-white hover:bg-[#064b43]"
                            }`}
                          >
                            {isBooked ? (
                              <>
                                <CheckCircle2 size={15} />
                                Booked
                              </>
                            ) : isBooking ? (
                              <>
                                <CircularProgress size={14} color="inherit" />
                                Booking
                              </>
                            ) : (
                              <>
                                <CalendarPlus size={15} />
                                Book consultation
                              </>
                            )}
                          </button>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #e2e8f0",
          px: 3,
          py: 2,
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialistsDialog;